import { fetchFastApi } from "../../api/fastapi";
import { useHookCacheQuery, type CacheKey } from "../../lib/hookCache";
import {
  ControlSessionEnvelope,
  controlSessionEnvelopeSchema,
} from "../../models/audit/controlSession";

/**
 * Which entity heads the session envelope. Control sessions live under
 * `/controls/v2`, work step sessions under `/work-steps` — the envelope shape
 * is shared (see `controlSessionEnvelopeSchema`), only the head entity and
 * the API roots differ.
 */
export type SessionKind = "control" | "work_step";

/**
 * Exact cache key of a session envelope entry.
 *
 * Both kinds live under the `["control-session", id]` prefix on purpose: the
 * shared pages/components invalidate that prefix (and the sibling
 * `["control-session", id, "status"]` key) after uploads/runs, and prefix
 * invalidation must keep hitting the envelope regardless of kind. The
 * work-step entry gets a distinct trailing element so the two kinds can never
 * share an exact entry.
 */
export function sessionEnvelopeCacheKey(
  controlSessionId: string,
  kind: SessionKind,
): CacheKey {
  return kind === "work_step"
    ? ["control-session", controlSessionId, "work-step-envelope"]
    : ["control-session", controlSessionId];
}

/**
 * Lightweight page-skeleton hook for the Phase 2 envelope endpoints:
 * `GET /controls/v2/control-session/{id}` (kind `"control"`, default) or
 * `GET /work-steps/work-step-session/{id}` (kind `"work_step"`).
 *
 * Returns a `ControlSessionEnvelope` (header fields + per-procedure envelopes
 * with counts) — explicitly NOT the heavy megaquery payload.
 */
export function useControlSessionEnvelope(
  controlSessionId: string | undefined,
  kind: SessionKind = "control",
) {
  return useHookCacheQuery<ControlSessionEnvelope>({
    key: sessionEnvelopeCacheKey(controlSessionId ?? "", kind),
    fetcher: async () => {
      const path =
        kind === "work_step"
          ? `/work-steps/work-step-session/${controlSessionId}`
          : `/controls/v2/control-session/${controlSessionId}`;
      const response = await fetchFastApi({
        method: "GET",
        path,
      });
      return controlSessionEnvelopeSchema.parse(response);
    },
    enabled: !!controlSessionId,
  });
}
